/**
 * ghost-cleanup.ts — Fix 3: one-time startup cleanup of IDB ghost records.
 *
 * Root cause: the old serverId derivation bug in InformeEditor wrote
 * { localId: <serverId>, serverId: undefined } whenever the editor was opened
 * by a server id (the most common case). This created "ghost" records that:
 *   - Show as "Pendiente" forever (en_cola with no outbox entry).
 *   - Duplicate the canonical synced record (which has localId=originalUUID, serverId=serverUUID).
 *
 * Cleanup strategy (conservative):
 *   A. A ghost record has no serverId of its own (serverId === undefined).
 *   B. Its localId matches the serverId of another record in the store.
 *   C. It has NO outbox entry (no pending local edits that would be lost on deletion).
 *
 * Called once at app startup (see useFlushOnReconnect / TechnicianPanel mount).
 * Safe to call multiple times — returns the number of records deleted.
 */

import { getDb } from "../stores/db.js";
import { enqueue } from "./outbox.js";
import type { LocalReport, OutboxItem, SyncInformeItem } from "../types.js";

/**
 * Re-enqueue "stuck" en_cola reports that lost their outbox entry.
 *
 * Root cause: enqueueReport writes the report record and the outbox item in TWO
 * separate IndexedDB transactions. If the app is closed/crashes between them (or
 * the outbox write fails), the report is persisted as en_cola but has NO outbox
 * entry. Flush then finds nothing to send → the report shows "En cola" / "Pendiente"
 * forever and the manual sync reports "no había informes pendientes".
 *
 * cleanupGhostReports does NOT cover this: that ghost has localId === another
 * record's serverId (an editor-derivation artifact). A freshly created offline
 * report has its own UUID localId and no serverId, so it falls through.
 *
 * Recovery (non-destructive — the report carries real field data + firmas): rebuild
 * the SyncInformeItem from the local record and enqueue it so the next flush syncs it.
 *
 * Rules:
 *   - Only en_cola reports (borrador = still being edited; sincronizado/enviado = done).
 *   - Only when NO outbox entry already exists (never clobber pending/inflight/error work).
 *
 * Safe to call multiple times — idempotent. Returns the number of reports re-enqueued.
 */
export async function requeueOrphanEnColaReports(): Promise<number> {
  const db = await getDb();
  const allReports = (await db.getAll("reports")) as LocalReport[];
  let requeued = 0;

  for (const report of allReports) {
    if (report.estado !== "en_cola") continue;

    const outboxEntry = (await db.get("outbox", report.localId)) as OutboxItem | undefined;
    if (outboxEntry !== undefined) continue; // already queued — leave it alone

    const item: SyncInformeItem = {
      localId: report.localId,
      serverId: report.serverId,
      tipo: report.tipo,
      clienteId: report.clienteId,
      cliente: report.cliente,
      parentId: report.parentLocalId,
      estado: report.estado, // "en_cola"
      payload: report.payload,
      updatedAt: report.updatedAt,
    };
    await enqueue(item);
    requeued++;
  }

  return requeued;
}

/**
 * Remove ghost records: rows with no serverId whose localId equals another record's serverId,
 * AND that have no outbox entry (no unsynced local edits).
 *
 * @returns number of records deleted
 */
/**
 * Remove orphan outbox items: entries whose corresponding local report is already
 * synchronized (estado sincronizado or enviado) or no longer exists in IDB.
 *
 * Orphan outbox items arise when a flush cycle succeeds (report persisted as
 * sincronizado) but crashes before calling outbox.remove() — a classic crash-
 * between-two-writes scenario. They show up as "pendiente" in SyncStatusPanel
 * even though the header EN COLA count is 0 (the IDB report is not en_cola).
 *
 * Rules:
 *   - Only considers pending/error items. Inflight items are being actively
 *     processed; failed items require an explicit manual "Reintentar" reset.
 *   - If the report is missing from IDB → orphan (remove).
 *   - If the report's estado is sincronizado or enviado → orphan (remove).
 *   - If the report is en_cola or borrador → NOT orphaned (active local work).
 *
 * Safe to call multiple times — idempotent. Returns the number of entries deleted.
 *
 * ponytail: called at startup alongside cleanupGhostReports; ceiling = items that
 * accumulate during a running session (post-crash). Those are caught by the next
 * startup. Upgrade = call from flush.ts reconcile on "created"/"updated" to guarantee
 * removal even when two writes straddle a crash.
 */
export async function cleanupOrphanOutboxItems(): Promise<number> {
  const db = await getDb();
  const allOutbox = (await db.getAll("outbox")) as OutboxItem[];
  let removed = 0;

  for (const item of allOutbox) {
    // Skip items that are actively being processed or held for manual retry.
    if (item.syncState !== "pending" && item.syncState !== "error") continue;

    const report = (await db.get("reports", item.localId)) as LocalReport | undefined;

    // Case 1: No local report found — the outbox entry has no backing record.
    if (!report) {
      await db.delete("outbox", item.localId);
      removed++;
      continue;
    }

    // Case 2: Report is already in a terminal-sync state — outbox entry is stale.
    // A pending outbox entry for a sincronizado/enviado report means the previous
    // flush succeeded but the outbox.remove() did not complete (crash, power loss).
    if (report.estado === "sincronizado" || report.estado === "enviado") {
      await db.delete("outbox", item.localId);
      removed++;
    }
  }

  return removed;
}

/**
 * Discard reports that could NOT be synced after exhausting all retries (terminal
 * "failed" outbox items). Removes the outbox entry, the local report record, and any
 * queued blobs (firmas/photos) for it.
 *
 * DESTRUCTIVE and irreversible — the caller MUST confirm with the user first. This is
 * the "Limpiar" escape hatch for a report the server keeps rejecting (e.g. a broken
 * parent link) so it stops blocking the queue. Only touches terminal failed items, so
 * normal borradores and auto-retrying pending/error work are never removed.
 *
 * Returns the number of reports discarded.
 */
export async function discardTerminalFailed(): Promise<number> {
  const db = await getDb();
  const failed = (await db.getAllFromIndex("outbox", "by-syncState", "failed")) as OutboxItem[];
  let removed = 0;

  for (const item of failed) {
    const blobs = (await db.getAllFromIndex("blobs", "by-owner", item.localId)) as Array<{
      id: string;
    }>;
    for (const b of blobs) await db.delete("blobs", b.id);
    await db.delete("outbox", item.localId);
    await db.delete("reports", item.localId);
    removed++;
  }

  return removed;
}

export async function cleanupGhostReports(): Promise<number> {
  const db = await getDb();

  const allReports = (await db.getAll("reports")) as LocalReport[];

  // Collect all known serverIds from records that DO have one.
  const serverIds = new Set<string>();
  for (const r of allReports) {
    if (r.serverId) serverIds.add(r.serverId);
  }

  let deleted = 0;

  for (const report of allReports) {
    // Ghost criterion A+B: no own serverId, localId is another record's serverId.
    if (report.serverId !== undefined || !serverIds.has(report.localId)) continue;

    // Ghost criterion C: no outbox entry → safe to delete.
    const outboxEntry = (await db.get("outbox", report.localId)) as OutboxItem | undefined;
    if (outboxEntry !== undefined) continue; // has pending edits — do NOT delete

    await db.delete("reports", report.localId);
    deleted++;
  }

  return deleted;
}
